import { Type } from "class-transformer";
import { Attribute } from "./Attribute";
import { AttributeOption } from "./AttributeOption";
import { Comparison } from "./Comparison/Comparison";
import { Coverage } from "./Coverage";
import { CoverageOption } from "./CoverageOption";
import { Driver } from "./Driver";
import { EquipmentComparison } from "./PolicyLayer";

export class CoverageLinkedDriver implements EquipmentComparison.IIsComparable {
    #initialized = false;
    static init(options: { coverage?: Coverage; linkedDriver: CoverageLinkedDriver }): CoverageLinkedDriver {
        const { linkedDriver } = options;
        if (linkedDriver instanceof CoverageLinkedDriver && linkedDriver.#initialized) return linkedDriver;

        const e = linkedDriver instanceof CoverageLinkedDriver ? linkedDriver : Object.assign(new CoverageLinkedDriver(), linkedDriver);
        const computedOptions = { coverage: options.coverage, linkedDriver: e };
        e.attributes = CoverageLinkedDriver.#getAttributes(computedOptions);
        e.yearsExperience = CoverageLinkedDriver.getYearsExperience(computedOptions);
        e.isOwnerOperator = CoverageLinkedDriver.getIsOwnerOperator(computedOptions);
        e.dateOfHire = CoverageLinkedDriver.getDateOfHire(computedOptions);

        e.#initialized = true;
        return e;
    }

    static getAttributes({ coverage, linkedDriver }: { coverage?: Coverage; linkedDriver: CoverageLinkedDriver }): Attribute[] {
        if (!coverage) return [];
        return coverage.attributes.filter((a) => a.driverId === linkedDriver.driverId);
    }

    static hasAttribute({
        coverage,
        linkedDriver,
        attributeOptionId,
    }: {
        coverage?: Coverage;
        linkedDriver: CoverageLinkedDriver;
        attributeOptionId: AttributeOption.Id;
    }): boolean {
        const attributes = CoverageLinkedDriver.#getAttributes({ coverage, linkedDriver });
        return attributes.some((a) => a.attributeOptionId === attributeOptionId);
    }

    static #getAttributes({ coverage, linkedDriver }: { coverage?: Coverage; linkedDriver: CoverageLinkedDriver }): Attribute[] {
        return linkedDriver.attributes || CoverageLinkedDriver.getAttributes({ coverage, linkedDriver });
    }

    static getYearsExperience(options: { coverage?: Coverage; linkedDriver: CoverageLinkedDriver }): number | null {
        const attr = CoverageLinkedDriver.#getAttributes(options).find(
            (a) => a.attributeOptionId === AttributeOption.Id.DRIVER_YEARS_EXPERIENCE
        );
        return attr?.valueNumber || null;
    }

    static getIsOwnerOperator(options: { coverage?: Coverage; linkedDriver: CoverageLinkedDriver }): boolean {
        return CoverageLinkedDriver.hasAttribute({ ...options, attributeOptionId: AttributeOption.Id.UNIT_OWNER_OPERATOR });
    }

    static getDateOfHire(options: { coverage?: Coverage; linkedDriver: CoverageLinkedDriver }): string | null {
        const attr = CoverageLinkedDriver.#getAttributes(options).find((a) => a.attributeOptionId === AttributeOption.Id.DRIVER_HIRE_DATE);
        return attr?.valueText || null;
    }

    static compare({
        base,
        compare,
        comparisonLayer,
        baseLayer,
        baseLayerItemIds,
        comparisonLayerItemIds,
    }: Comparison.IOptions<CoverageLinkedDriver> & {
        baseLayerItemIds?: Set<number>;
        comparisonLayerItemIds?: Set<number>;
    }): Map<CoverageOption.Id, CoverageLinkedDriver.IComparisonReturn> {
        const map = new Map<CoverageOption.Id, CoverageLinkedDriver.IComparisonReturn>();

        if (compare) {
            for (const [cvgOptId, linkedTractor] of Object.entries(compare) as unknown as [CoverageOption.Id, CoverageLinkedDriver][]) {
                const from = base?.[cvgOptId];
                const to = linkedTractor;
                map.set(
                    cvgOptId,
                    CoverageLinkedDriver.#compare({
                        base: from,
                        compare: to,
                        comparisonLayer,
                        baseLayer,
                        baseLayerItemIds,
                        comparisonLayerItemIds,
                    })
                );
            }
        }

        if (base) {
            for (const [cvgOptId, linkedTractor] of Object.entries(base) as unknown as [CoverageOption.Id, CoverageLinkedDriver][]) {
                if (map.has(cvgOptId)) continue;
                const from = linkedTractor;
                const to = compare?.[cvgOptId];
                map.set(
                    cvgOptId,
                    CoverageLinkedDriver.#compare({
                        base: from,
                        compare: to,
                        comparisonLayer,
                        baseLayer,
                        baseLayerItemIds,
                        comparisonLayerItemIds,
                    })
                );
            }
        }

        return map;
    }

    static #compare({
        base,
        compare,
        comparisonLayer,
        baseLayer,
        baseLayerItemIds,
        comparisonLayerItemIds,
    }: Partial<Comparison.IOptions.Entity<CoverageLinkedDriver>> & {
        baseLayerItemIds?: Set<number>;
        comparisonLayerItemIds?: Set<number>;
    }): CoverageLinkedDriver.IComparisonReturn {
        const comparison = new Comparison(CoverageLinkedDriver, base, compare) as CoverageLinkedDriver.IComparisonReturn;
        comparison.setField("driverId", compare?.driverId || base?.driverId);
        comparison.setField("driver", compare?.driver || base?.driver);

        if (!base) {
            if (baseLayer) {
                const allPreviousBaIds =
                    baseLayerItemIds || new Set(baseLayer.policy.coverages.flatMap((cvg) => cvg.linkedDrivers.map((l) => l.driverId)));

                compare.comparison = {
                    isNewToPolicy: !allPreviousBaIds.has(compare.driverId),
                };
            }

            return comparison.setNew({
                obj: compare,
                description: `Add driver ${compare?.driver?.name || compare?.driverId}`,
                subComparison: comparison as Comparison<unknown>,
                label: "Driver",
            });
        }

        if (!compare) {
            if (comparisonLayer) {
                const allPreviousBaIds =
                    comparisonLayerItemIds ||
                    new Set(comparisonLayer.policy.coverages.flatMap((cvg) => cvg.linkedDrivers.map((l) => l.driverId)));

                base.comparison = {
                    isDeletedFromPolicy: !allPreviousBaIds.has(base.driverId),
                };
            }

            return comparison.setDelete({
                obj: base,
                description: `Delete driver ${base?.driver?.name || base?.driverId}`,
                subComparison: comparison as Comparison<unknown>,
                label: "Driver",
            });
        }

        [
            {
                key: "yearsExperience",
                label: "Years of Experience",
                transform: (v) => v,
            },
            {
                key: "isOwnerOperator",
                label: "IC",
                transform: (v) => (v ? "Yes" : "No"),
                getActionLabel: (v): Comparison.ActionLabel => (v ? "add" : "remove"),
            },
            {
                key: "dateOfHire",
                label: "Date of Hire",
                transform: (v) => v,
            },
        ].forEach(({ key, label, transform, getActionLabel }) => {
            if (base[key] !== compare[key]) {
                comparison.addDiff({
                    type: "change",
                    description: `Update ${label} from ${transform(base[key])} to ${transform(compare[key])}`,
                    label,
                    actionLabel: getActionLabel?.(compare[key]),
                    priority: null,
                    fieldName: key as keyof CoverageLinkedDriver,
                    isArrayField: false,
                    value: {
                        from: base[key],
                        to: compare[key],
                        base,
                        compare,
                    },
                });
            }
        });

        return comparison;
    }

    id: number;
    createdDate: Date;

    driverId: number;
    @Type(() => Driver)
    driver?: Driver;

    attributes?: Attribute[];
    yearsExperience?: number;
    isOwnerOperator?: boolean;
    dateOfHire?: string;

    coverageId: number;
    coverageCreatedDate: Date;
    coverage?: Coverage;

    comparison?: {
        isNewToPolicy?: boolean;
        isDeletedFromPolicy?: boolean;
    };

    toString(): string {
        return this.driver?.toString() || `CoverageLinkedDriver: ${this.driverId}`;
    }

    compare(
        comparisonLinkedDriver: CoverageLinkedDriver,
        options?: Comparison.getBaseAndComparisonObjects.IOptions
    ): Comparison<CoverageLinkedDriver> {
        return CoverageLinkedDriver.#compare(
            Comparison.getBaseAndComparisonObjects(
                {
                    initiatorEntity: this,
                    comparisonEntity: comparisonLinkedDriver,
                },
                options
            )
        );
    }
}

export namespace CoverageLinkedDriver {
    export interface IComparisonReturn extends Comparison<CoverageLinkedDriver> {
        driverId: number;
        driver?: Driver;
    }
}
